import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { startOfDay, subDays } from 'date-fns'
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  Eye,
  Megaphone,
  PauseCircle,
  Pin,
  School,
  UserRoundCheck,
  UsersRound,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { announcementApi, type Announcement } from '@/api/announcements'
import { memberApi, type Member } from '@/api/b2b'
import { lcTimeslotApi, type Timeslot } from '@/api/lc'
import { learnerStatusApi, type LearnerStatusCode } from '@/api/learnerStatus'
import { AnnouncementDetailModal } from '@/components/admin/AnnouncementDetailModal'
import type { DaySchedule, ScheduleData } from '@/types/calendar'
import { learnerHandle } from '@/utils/learnerEmail'

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
const DASH = '-'

interface DashboardMetricProps {
  label: string
  value: number
  unit: string
  description: string
  icon: React.ElementType
  accentClassName: string
  iconClassName: string
  to: string
}

interface RecentStudent {
  member: Member
  status: Extract<LearnerStatusCode, 'WAITING' | 'ENROLLED'>
}

function parseSchedule(schedule: string): ScheduleData {
  try {
    const parsed: unknown = JSON.parse(schedule)
    return parsed && typeof parsed === 'object' ? parsed as ScheduleData : {}
  } catch {
    return {}
  }
}

function DashboardMetric({
  label,
  value,
  unit,
  description,
  icon: Icon,
  accentClassName,
  iconClassName,
  to,
}: DashboardMetricProps) {
  return (
    <Link
      to={to}
      className={`group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_12px_30px_rgba(15,23,42,0.08)] ${accentClassName}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-600">{label}</p>
          <p className="mt-3 flex items-end gap-1.5 text-gray-950">
            <span className="text-4xl font-bold tracking-tight tabular-nums">{value.toLocaleString()}</span>
            <span className="pb-1 text-sm font-semibold text-gray-500">{unit}</span>
          </p>
          <p className="mt-3 text-xs leading-5 text-gray-500">{description}</p>
        </div>
        <span className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${iconClassName}`}>
          <Icon className="size-5" aria-hidden="true" />
        </span>
      </div>
      <ChevronRight className="absolute bottom-5 right-5 size-4 translate-x-1 text-gray-300 opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" aria-hidden="true" />
    </Link>
  )
}

function DashboardSectionHeader({
  titleId,
  eyebrow,
  title,
  description,
  icon: Icon,
  action,
}: {
  titleId: string
  eyebrow: string
  title: string
  description: string
  icon: React.ElementType
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-4 border-b border-gray-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
          <Icon className="size-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary-600">{eyebrow}</p>
          <h2 id={titleId} className="mt-1 text-lg font-bold tracking-tight text-gray-950">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
      </div>
      {action}
    </div>
  )
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation(['admin', 'common'])

  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)
  const [isAnnouncementLoading, setIsAnnouncementLoading] = useState(false)

  const handleViewAnnouncement = async (announcementId: number) => {
    setIsAnnouncementLoading(true)
    try {
      const detail = await announcementApi.getById(announcementId)
      setSelectedAnnouncement(detail)
    } catch {
      toast.error(t('announcement.toast.loadFailed'))
    } finally {
      setIsAnnouncementLoading(false)
    }
  }

  const membersQuery = useQuery({
    queryKey: ['dashboard', 'members'],
    queryFn: () => memberApi.getAll(),
  })
  const statusesQuery = useQuery({
    queryKey: ['dashboard', 'learner-statuses'],
    queryFn: () => learnerStatusApi.getStatuses(),
  })
  const timeslotsQuery = useQuery({
    queryKey: ['dashboard', 'timeslots'],
    queryFn: () => lcTimeslotApi.getAll(),
  })
  const announcementsQuery = useQuery({
    queryKey: ['dashboard', 'announcements', 'latest'],
    queryFn: () => announcementApi.getList({ page: 0, size: 3, scope: 'all', sort: 'latest' }),
  })

  const members = membersQuery.data?.data?.data ?? []
  const learnerStatuses = statusesQuery.data?.data?.data ?? []
  const timeslots = timeslotsQuery.data?.data?.data ?? []
  const announcements = announcementsQuery.data?.announcements ?? []
  const locale = i18n.language === 'en' ? 'en-US' : 'ko-KR'

  const statusByUserId = useMemo(
    () => new Map(learnerStatuses.map(({ userId, status }) => [userId, status])),
    [learnerStatuses],
  )

  const statusCounts = useMemo(() => {
    const counts: Record<LearnerStatusCode, number> = {
      WAITING: 0,
      ENROLLED: 0,
      ON_LEAVE: 0,
      WITHDRAWN: 0,
    }
    learnerStatuses.forEach(({ status }) => {
      counts[status] += 1
    })
    return counts
  }, [learnerStatuses])

  const activeTimeslots = useMemo(
    () => timeslots.filter((timeslot) => timeslot.isActive),
    [timeslots],
  )

  const scheduledTimeslots = useMemo(
    () => activeTimeslots.map((timeslot) => ({ timeslot, schedule: parseSchedule(timeslot.schedule) })),
    [activeTimeslots],
  )

  const weeklySchedule = useMemo(
    () => DAY_KEYS.map((dayKey) => ({
      dayKey,
      classes: scheduledTimeslots
        .reduce<Array<{ timeslot: Timeslot; daySchedule: DaySchedule }>>((result, item) => {
          const daySchedule = item.schedule[dayKey]
          if (daySchedule?.startTime) result.push({ timeslot: item.timeslot, daySchedule })
          return result
        }, [])
        .sort((a, b) => a.daySchedule.startTime.localeCompare(b.daySchedule.startTime)),
    })),
    [scheduledTimeslots],
  )

  const weeklyClassCount = useMemo(
    () => weeklySchedule.reduce((count, day) => count + day.classes.length, 0),
    [weeklySchedule],
  )

  const recentStudents = useMemo<RecentStudent[]>(() => {
    const cutoff = startOfDay(subDays(new Date(), 30)).getTime()

    return members
      .reduce<RecentStudent[]>((result, member) => {
        const status = statusByUserId.get(member.userId)
        const registeredAt = new Date(member.createdAt).getTime()
        if ((status === 'WAITING' || status === 'ENROLLED') && registeredAt >= cutoff) {
          result.push({ member, status })
        }
        return result
      }, [])
      .sort((a, b) => new Date(b.member.createdAt).getTime() - new Date(a.member.createdAt).getTime())
  }, [members, statusByUserId])

  const metrics = [
    {
      label: t('dashboard.stats.activeClasses'),
      value: activeTimeslots.length,
      unit: t('dashboard.stats.classUnit'),
      description: t('dashboard.stats.activeClassesDescription'),
      icon: School,
      accentClassName: 'border-l-4 border-l-primary-500',
      iconClassName: 'bg-primary-50 text-primary-600',
      to: '/admin/classes?tab=timeslots',
    },
    {
      label: t('dashboard.stats.enrolledStudents'),
      value: statusCounts.ENROLLED,
      unit: t('dashboard.stats.studentUnit'),
      description: t('dashboard.stats.enrolledStudentsDescription'),
      icon: UserRoundCheck,
      accentClassName: 'border-l-4 border-l-emerald-500',
      iconClassName: 'bg-emerald-50 text-emerald-600',
      to: '/admin/members',
    },
    {
      label: t('dashboard.stats.onLeaveStudents'),
      value: statusCounts.ON_LEAVE,
      unit: t('dashboard.stats.studentUnit'),
      description: t('dashboard.stats.onLeaveStudentsDescription'),
      icon: PauseCircle,
      accentClassName: 'border-l-4 border-l-amber-400',
      iconClassName: 'bg-amber-50 text-amber-600',
      to: '/admin/members',
    },
  ]

  const getGradeLevelLabel = (gradeLevel: string | null | undefined) =>
    gradeLevel ? t(`dashboard.gradeLevel.${gradeLevel}`, { defaultValue: gradeLevel }) : DASH

  const getGenderLabel = (gender: string | null | undefined) =>
    gender ? t(`dashboard.gender.${gender}`, { defaultValue: gender }) : DASH

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString)
    const year = String(date.getFullYear()).slice(-2)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatDateTime = (dateString: string) =>
    new Intl.DateTimeFormat(locale, {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(dateString))

  const isDataLoading = membersQuery.isLoading || statusesQuery.isLoading || timeslotsQuery.isLoading
  const hasDataError = membersQuery.isError || statusesQuery.isError || timeslotsQuery.isError

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6 pb-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary-600">
            <span className="size-1.5 rounded-full bg-primary-500" />
            {t('dashboard.eyebrow')}
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-950">{t('dashboard.title')}</h1>
          <p className="mt-2 text-sm text-gray-500">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 self-start rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-600 shadow-sm sm:self-auto">
          <CalendarDays className="size-4 text-primary-500" aria-hidden="true" />
          {new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' }).format(new Date())}
        </div>
      </header>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)]" aria-labelledby="dashboard-announcements-title">
        <DashboardSectionHeader
          titleId="dashboard-announcements-title"
          eyebrow={t('dashboard.announcements.eyebrow')}
          title={t('dashboard.announcements.title')}
          description={t('dashboard.announcements.description')}
          icon={Megaphone}
        />
        <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full">
            <thead className="bg-gray-50/80">
              <tr>
                {(['type', 'title', 'createdAt', 'viewCount'] as const).map((column) => (
                  <th key={column} className="border-b border-gray-200 px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    {t(`dashboard.announcements.column.${column}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {announcementsQuery.isLoading ? (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-sm text-gray-400">{t('label.loading', { ns: 'common' })}</td></tr>
              ) : announcements.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-sm text-gray-400">{t('dashboard.announcements.empty')}</td></tr>
              ) : announcements.map((announcement) => (
                <tr key={announcement.announcementId} className="transition-colors hover:bg-gray-50/70">
                  <td className="whitespace-nowrap px-5 py-3.5">
                    {announcement.isPinned ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700 ring-1 ring-inset ring-amber-200">
                        <Pin className="size-3" aria-hidden="true" />{t('dashboard.announcements.type.important')}
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-600">{t('dashboard.announcements.type.general')}</span>
                    )}
                  </td>
                  <td className="max-w-xl px-5 py-3.5">
                    <button
                      type="button"
                      onClick={() => handleViewAnnouncement(announcement.announcementId)}
                      disabled={isAnnouncementLoading}
                      className="text-left font-semibold text-gray-900 hover:text-primary-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-wait"
                    >
                      {announcement.title}
                    </button>
                    {announcement.source === 'org_admin' ? (
                      <span className="ml-2 inline-flex rounded bg-primary-50 px-1.5 py-0.5 text-[10px] font-bold text-primary-600">{t('dashboard.announcements.badge.center')}</span>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-sm tabular-nums text-gray-500">{formatDateTime(announcement.createdAt)}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-sm tabular-nums text-gray-500">
                    <span className="inline-flex items-center gap-1.5"><Eye className="size-3.5" aria-hidden="true" />{announcement.viewCount.toLocaleString()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="dashboard-operations-title">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 id="dashboard-operations-title" className="text-base font-bold text-gray-950">{t('dashboard.operations.title')}</h2>
            <p className="mt-1 text-xs text-gray-500">{t('dashboard.operations.description')}</p>
          </div>
          {isDataLoading ? <span className="text-xs font-medium text-gray-400">{t('label.loading', { ns: 'common' })}</span> : null}
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {metrics.map((metric) => <DashboardMetric key={metric.label} {...metric} />)}
        </div>
        {hasDataError ? <p className="mt-2 text-sm text-red-600">{t('dashboard.error.loadFailed')}</p> : null}
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)]" aria-labelledby="dashboard-schedule-title">
        <DashboardSectionHeader
          titleId="dashboard-schedule-title"
          eyebrow={t('dashboard.schedule.eyebrow')}
          title={t('dashboard.schedule.title')}
          description={t('dashboard.schedule.description')}
          icon={CalendarDays}
          action={(
            <span className="inline-flex items-center rounded-full border border-primary-100 bg-primary-50 px-3 py-1.5 text-xs font-bold text-primary-700">
              {t('dashboard.schedule.weeklyClassCount', { count: weeklyClassCount })}
            </span>
          )}
        />

        <div className="overflow-x-auto">
          <div className="grid min-w-[980px] grid-cols-7 divide-x divide-gray-100">
            {weeklySchedule.map(({ dayKey, classes }, index) => {
              const isSaturday = index === 5
              const isSunday = index === 6

              return (
                <div key={dayKey} className={`min-h-56 p-3 ${isSaturday || isSunday ? 'bg-gray-50/50' : 'bg-white'}`}>
                  <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-3">
                    <span className={`flex size-9 items-center justify-center rounded-xl text-sm font-bold ${isSaturday ? 'bg-primary-50 text-primary-600' : isSunday ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-700'}`}>
                      {t(`dashboard.schedule.days.${dayKey}`)}
                    </span>
                    <span className="text-[10px] font-bold text-gray-400">{t('dashboard.schedule.classCount', { count: classes.length })}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {classes.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-gray-200 px-2 py-5 text-center text-xs text-gray-400">
                        {t('dashboard.schedule.empty')}
                      </p>
                    ) : classes.map(({ timeslot, daySchedule }) => (
                      <Link
                        key={`${dayKey}-${timeslot.timeslotId}`}
                        to="/admin/classes?tab=timeslots"
                        className="group rounded-xl border border-primary-100 bg-primary-50/70 px-3 py-2.5 transition hover:-translate-y-0.5 hover:border-primary-200 hover:bg-primary-50 hover:shadow-sm"
                      >
                        <p className="truncate text-xs font-bold text-gray-900 group-hover:text-primary-700">{timeslot.name}</p>
                        <div className="mt-1.5 flex flex-col gap-1 text-[10px] font-medium text-gray-500">
                          <span className="flex items-center gap-1"><Clock3 className="size-3" aria-hidden="true" />{daySchedule.startTime}</span>
                          <span className="flex items-center gap-1"><UsersRound className="size-3" aria-hidden="true" />{t('dashboard.schedule.studentCount', { count: timeslot.currentStudents })}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.05)]" aria-labelledby="dashboard-students-title">
        <DashboardSectionHeader
          titleId="dashboard-students-title"
          eyebrow={t('dashboard.students.eyebrow')}
          title={t('dashboard.students.title')}
          description={t('dashboard.students.description')}
          icon={UsersRound}
        />
        <div className="overflow-x-auto">
          <table className="min-w-[1180px] w-full">
            <thead className="bg-gray-50/80">
              <tr>
                {(['status', 'name', 'id', 'grade', 'gender', 'guardianPhone', 'course', 'class', 'registeredAt'] as const).map((column) => (
                  <th key={column} className="border-b border-gray-200 px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    {t(`dashboard.students.column.${column}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {membersQuery.isLoading || statusesQuery.isLoading ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-400">{t('label.loading', { ns: 'common' })}</td></tr>
              ) : recentStudents.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-sm text-gray-400">{t('dashboard.students.empty')}</td></tr>
              ) : recentStudents.map(({ member, status }) => (
                <tr key={member.userId} className="transition-colors hover:bg-gray-50/70">
                  <td className="whitespace-nowrap px-4 py-3.5">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${status === 'WAITING' ? 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200' : 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200'}`}>
                      {t(`dashboard.students.status.${status}`)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-sm font-bold text-gray-900">{member.fullName}</td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-sm text-gray-600">{learnerHandle(member.email)}</td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-sm text-gray-600">{getGradeLevelLabel(member.gradeLevel)}</td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-sm text-gray-600">{getGenderLabel(member.gender)}</td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-sm tabular-nums text-gray-600">{member.guardian?.phone || DASH}</td>
                  <td className="max-w-48 truncate px-4 py-3.5 text-sm text-gray-600">{member.linkedProductName || DASH}</td>
                  <td className="max-w-48 truncate px-4 py-3.5 text-sm text-gray-600">{member.classNames?.join(', ') || DASH}</td>
                  <td className="whitespace-nowrap px-4 py-3.5 text-sm tabular-nums text-gray-500">{formatShortDate(member.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {selectedAnnouncement && (
        <AnnouncementDetailModal
          announcement={selectedAnnouncement}
          onClose={() => setSelectedAnnouncement(null)}
        />
      )}
    </div>
  )
}
